<template>
    <div class="flex flex-col">
        <!-- Content -->
        <div class="p-6">
            <form @submit.prevent="updateGuide">
                <!-- Guide Info -->
                <div class="mb-6 p-4 bg-gray-50 rounded-lg">
                    <div class="flex items-center mb-3">
                        <div class="p-2 bg-white rounded-lg shadow-sm mr-3">
                            <font-awesome-icon :icon="getFileIcon(guide.file_type)"
                                :class="getFileIconColor(guide.file_type)" class="text-xl" />
                        </div>
                        <div>
                            <p class="font-medium text-gray-900">{{ guide.original_filename }}</p>
                            <p class="text-sm text-gray-500">{{ guide.formatted_file_size }}</p>
                        </div>
                    </div>
                    <p class="text-xs text-gray-500">
                        Uploaded by {{ guide.uploadedBy.name }} on {{ formatDate(guide.created_at) }}
                    </p>
                </div>
                <!-- Document Name -->
                <div class="mb-6">
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                        Document Name <span class="text-red-500">*</span>
                    </label>
                    <input v-model="form.name" type="text" required
                        placeholder="Enter a descriptive name for this document"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        :class="{ 'border-red-500': errors.name }" />
                    <p v-if="errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</p>
                </div>
                <!-- Description -->
                <div class="mb-6">
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                        Description
                    </label>
                    <textarea v-model="form.description" rows="3" placeholder="Optional description of the document"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"></textarea>
                </div>
            </form>
        </div>


        <!-- Footer -->
        <div class="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
            <base-button @click="$emit('close')" class="bg-gray-300 hover:bg-gray-400 text-gray-700">
                Cancel
            </base-button>
            <base-button @click="updateGuide" :disabled="!isFormValid || updating"
                class="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed">
                <div class="h-full flex justify-center items-center" v-if="updating">
                    <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                </div>
                <font-awesome-icon v-else icon="save" class="mr-2" />
                {{ updating ? 'Updating...' : 'Update Document' }}
            </base-button>
        </div>

    </div>
</template>

<script>
import { gql } from 'graphql-tag';

export default {
    name: 'GuideEditModal',
    props: {
        guide: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            form: {
                name: '',
                description: ''
            },
            updating: false,
            errors: {}
        }
    },
    computed: {
        isFormValid() {
            return this.form.name.trim()
        }
    },
    mounted() {
        // Initialize form with current guide data
        this.form.name = this.guide.name
        this.form.description = this.guide.description || ''
    },
    methods: {
        async updateGuide() {
            if (!this.isFormValid) return

            this.updating = true
            this.errors = {}

            try {
                const response = await this.$apollo.mutate({
                    mutation: gql`
            mutation UpdateGuide($input: UpdateGuideInput!) {
              updateGuide(input: $input) {
                error
                error_code
                message
                data {
                  id
                  name
                  description
                  updated_at
                }
              }
            }
          `,
                    variables: {
                        input: {
                            id: this.guide.id,
                            name: this.form.name.trim(),
                            description: this.form.description.trim() || null
                        }
                    }
                })

                if (!response.data.updateGuide.error) {
                    this.$emit('updated', response.data.updateGuide.data)
                } else {
                    this.$swal({
                        icon: 'error',
                        title: 'Update Failed',
                        text: response.data.updateGuide.message || 'Failed to update document'
                    })
                }
            } catch (error) {
                console.error('Error updating guide:', error)

                // Handle specific GraphQL errors
                if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                    const graphQLError = error.graphQLErrors[0]
                    if (graphQLError.extensions && graphQLError.extensions.validation) {
                        // Handle validation errors
                        const validationErrors = graphQLError.extensions.validation
                        Object.keys(validationErrors).forEach(field => {
                            this.errors[field] = validationErrors[field][0]
                        })
                    } else {
                        this.$swal({
                            icon: 'error',
                            title: 'Update Failed',
                            text: graphQLError.message || 'Failed to update document'
                        })
                    }
                } else {
                    this.$swal({
                        icon: 'error',
                        title: 'Update Failed',
                        text: 'Failed to update document. Please try again.'
                    })
                }
            } finally {
                this.updating = false
            }
        },
        getFileIcon(fileType) {
            const iconMap = {
                'application/pdf': 'file-pdf',
                'application/msword': 'file-word',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'file-word',
                'application/vnd.ms-excel': 'file-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'file-excel',
                'application/vnd.ms-powerpoint': 'file-powerpoint',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'file-powerpoint',
                'text/plain': 'file-alt',
                'image/jpeg': 'file-image',
                'image/png': 'file-image',
                'image/gif': 'file-image'
            }
            return iconMap[fileType] || 'file'
        },
        getFileIconColor(fileType) {
            const colorMap = {
                'application/pdf': 'text-red-500',
                'application/msword': 'text-blue-500',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'text-blue-500',
                'application/vnd.ms-excel': 'text-green-500',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'text-green-500',
                'application/vnd.ms-powerpoint': 'text-orange-500',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'text-orange-500',
                'text/plain': 'text-gray-500',
                'image/jpeg': 'text-purple-500',
                'image/png': 'text-purple-500',
                'image/gif': 'text-purple-500'
            }
            return colorMap[fileType] || 'text-gray-500'
        },
        formatDate(dateString) {
            const date = new Date(dateString)
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        },
    },
}
</script>
